import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";

// CBAM brand — teal/forest primary, deep navy for headers, gold accent
const TEAL: [number, number, number] = [0, 71, 66];        // #004742 — brand primary
const TEAL_DARK: [number, number, number] = [0, 52, 48];   // deeper teal for cover
const NAVY: [number, number, number] = [27, 54, 93];       // #1B365D — section headers
const GOLD: [number, number, number] = [191, 158, 84];     // accent rule
const INK: [number, number, number] = [33, 37, 41];
const MUTED: [number, number, number] = [110, 117, 130];
const RULE: [number, number, number] = [225, 228, 233];
const EMERALD: [number, number, number] = [4, 120, 87];
const RED: [number, number, number] = [185, 28, 28];

interface FundPerformance { rank: number; name: string; return: string; }
interface FundPerformancePeriod {
  label: string;
  bestP: FundPerformance[]; worstP: FundPerformance[];
  bestR: FundPerformance[]; worstR: FundPerformance[];
}
interface PerformanceRow {
  portfolio: string; mtd: string; qtd: string; ytd: string; oneYear: string;
  afiBenchmark: string; vsAfiMtd: string; vsAfiQtd: string; vsAfiYtd: string; vsAfi1Y: string;
  internalBenchmark: string; vsIntMtd: string; vsIntQtd: string; vsIntYtd: string; vsInt1Y: string;
}
interface TradeRow {
  date: string; rawDate: string; manager: string; fundName: string; fundClass: string; isin: string;
  p1: string; p2: string; p3: string; p4: string; p5: string;
}
interface PositioningRow {
  sector: string;
  i: { position: string; value: string };
  ii: { position: string; value: string };
  iii: { position: string; value: string };
  iv: { position: string; value: string };
  v: { position: string; value: string };
}

export interface AdviserPdfData {
  updateDate: string;
  lastMonthCommentary: { date: string; text: string };
  latestCommentary: { date: string; text: string };
  performanceP: PerformanceRow[];
  performanceR: PerformanceRow[];
  fundPeriodLabel: string;
  bestP: FundPerformance[]; worstP: FundPerformance[];
  bestR: FundPerformance[]; worstR: FundPerformance[];
  fundPerformancePeriods?: FundPerformancePeriod[];
  tradeLogRangeLabel: string;
  trades: TradeRow[];
  positioning: PositioningRow[];
}

const PAGE = { w: 210, h: 297, m: 14 };
const FOOTER_GUARD = 22;

interface LogoAsset { data: string; w: number; h: number; }

let activeBodyPages: Set<number> | null = null;

function markBodyPage(doc: jsPDF) {
  const pageNumber = (doc as any).getCurrentPageInfo?.().pageNumber || doc.getNumberOfPages();
  activeBodyPages?.add(pageNumber);
}

function markBodyPageRange(doc: jsPDF, fromPage: number) {
  const pageNumber = (doc as any).getCurrentPageInfo?.().pageNumber || doc.getNumberOfPages();
  for (let p = fromPage; p <= pageNumber; p += 1) activeBodyPages?.add(p);
}

async function loadLogo(): Promise<LogoAsset | null> {
  try {
    const res = await fetch("/images/cbam-logo-updated.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = await new Promise<string | null>((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
    if (!data) return null;
    const dims = await new Promise<{ w: number; h: number }>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 300, h: 100 });
      img.src = data;
    });
    return { data, w: dims.w, h: dims.h };
  } catch { return null; }
}

/** Fit a logo into a max width/height while preserving aspect ratio, centred. */
function drawLogo(doc: jsPDF, logo: LogoAsset, x: number, y: number, maxW: number, maxH: number) {
  const ratio = logo.w / logo.h;
  let w = maxW, h = maxW / ratio;
  if (h > maxH) { h = maxH; w = maxH * ratio; }
  try { doc.addImage(logo.data, "PNG", x, y + (maxH - h) / 2, w, h, undefined, "FAST"); } catch {}
}

function pctColor(s?: string): [number, number, number] | undefined {
  if (!s) return undefined;
  const v = parseFloat(s.replace("%", ""));
  if (isNaN(v)) return undefined;
  if (v > 0) return EMERALD;
  if (v < 0) return RED;
  return undefined;
}

function positionFill(pos: string): [number, number, number] {
  switch (pos) {
    case "OVERWEIGHT": return [16, 138, 105];
    case "POSITIVE":   return [34, 160, 110];
    case "NEUTRAL":    return [130, 138, 150];
    case "NEGATIVE":   return [217, 119, 6];
    case "UNDERWEIGHT":return [185, 28, 28];
    default:           return [200, 204, 210];
  }
}

function drawHeader(doc: jsPDF, logo: LogoAsset | null, updateDate: string) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, w, 16, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 16, w, 0.8, "F");

  if (logo) drawLogo(doc, logo, PAGE.m, 2.5, 28, 11);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("CAPE BERKSHIRE ASSET MANAGEMENT", PAGE.m + 32, 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("Adviser-Client Presentation", PAGE.m + 32, 13);

  doc.setFontSize(7.5);
  doc.text(`Update: ${updateDate}`, w - PAGE.m, 9, { align: "right" });
  doc.text("Confidential", w - PAGE.m, 13, { align: "right" });
}

function drawFooter(doc: jsPDF, page: number, total: number) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const y = h - 10;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.2);
  doc.line(PAGE.m, y - 3, w - PAGE.m, y - 3);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text("Cape Berkshire Asset Management is a trading name of Ascot Wealth Management Ltd · FCA Firm Reference No. 551744", PAGE.m, y);
  doc.text(`Page ${page} of ${total}`, w - PAGE.m, y, { align: "right" });
}

function sectionTitle(doc: jsPDF, y: number, label: string, eyebrow?: string): number {
  markBodyPage(doc);
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...TEAL);
  doc.rect(PAGE.m, y, 2.2, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...TEAL);
  doc.text(label, PAGE.m + 5, y + 5.2);
  if (eyebrow) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(eyebrow, w - PAGE.m, y + 5.2, { align: "right" });
  }
  return y + 11;
}

function ensureSpace(doc: jsPDF, y: number, need: number, logo: LogoAsset | null, updateDate: string): number {
  const bottomLimit = doc.internal.pageSize.getHeight() - FOOTER_GUARD;
  if (y + need > bottomLimit) {
    doc.addPage();
    drawHeader(doc, logo, updateDate);
    return 24;
  }
  return y;
}

function paragraphs(doc: jsPDF, y: number, text: string, logo: LogoAsset | null, updateDate: string): number {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...INK);
  const width = PAGE.w - PAGE.m * 2 - 6;
  const paras = (text || "").split("\n").filter(p => p.trim());
  if (paras.length === 0) {
    markBodyPage(doc);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "italic");
    doc.text("No commentary available.", PAGE.m + 3, y);
    return y + 6;
  }
  for (const para of paras) {
    const lines = doc.splitTextToSize(para, width);
    for (const line of lines) {
      y = ensureSpace(doc, y, 5, logo, updateDate);
      markBodyPage(doc);
      doc.text(line, PAGE.m + 3, y);
      y += 4.6;
    }
    y += 2;
  }
  return y;
}

function newSection(doc: jsPDF, logo: LogoAsset | null, updateDate: string, currentY?: number, orientation: "portrait" | "landscape" = "portrait"): number {
  const pageNo = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const currentOrientation = pageW > pageH ? "landscape" : "portrait";
  const pageHasContent = currentY === undefined || currentY > 26;

  if (!pageHasContent && pageNo > 1 && currentOrientation !== orientation) {
    doc.deletePage(pageNo);
    doc.addPage("a4", orientation);
  } else if (pageHasContent || currentOrientation !== orientation) {
    doc.addPage("a4", orientation);
  }
  drawHeader(doc, logo, updateDate);
  return 24;
}

export async function generateAdviserPresentationPdf(data: AdviserPdfData) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  activeBodyPages = new Set<number>();
  const logo = await loadLogo();

  // ---------- COVER PAGE (brand teal, no blue) ----------
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, PAGE.w, PAGE.h, "F");
  doc.setFillColor(...TEAL_DARK);
  doc.rect(0, PAGE.h - 70, PAGE.w, 70, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, PAGE.h - 72, PAGE.w, 1.6, "F");

  if (logo) drawLogo(doc, logo, PAGE.m, 30, 70, 28);

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.text("Adviser-Client", PAGE.m, 110);
  doc.text("Presentation", PAGE.m, 122);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(225, 235, 232);
  doc.text("Cape Berkshire Asset Management", PAGE.m, 134);

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(PAGE.m, 142, PAGE.m + 40, 142);

  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text(`Generated  ${format(new Date(), "dd/MM/yyyy")}`, PAGE.m, 152);
  doc.text(`Data update  ${data.updateDate}`, PAGE.m, 158);

  doc.setFontSize(8.5);
  doc.setTextColor(225, 235, 232);
  doc.text("CONFIDENTIAL · FOR PROFESSIONAL USE", PAGE.m, PAGE.h - 56);
  doc.setFontSize(8);
  doc.text("Cape Berkshire Asset Management is a trading name of", PAGE.m, PAGE.h - 24);
  doc.text("Ascot Wealth Management Ltd · FCA FRN 551744", PAGE.m, PAGE.h - 18);
  doc.text("capeberkshire.co.uk", PAGE.m, PAGE.h - 12);

  // ---------- CONTENT ----------
  doc.addPage();
  drawHeader(doc, logo, data.updateDate);
  let y = 24;

  // Commentary
  y = sectionTitle(doc, y, "Director's Commentary");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...TEAL);
  doc.text(`Last Month  ·  ${data.lastMonthCommentary.date || "—"}`, PAGE.m + 3, y);
  y += 5;
  y = paragraphs(doc, y, data.lastMonthCommentary.text, logo, data.updateDate);
  y += 3;
  y = ensureSpace(doc, y, 18, logo, data.updateDate);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...TEAL);
  doc.text(`Latest  ·  ${data.latestCommentary.date || "—"}`, PAGE.m + 3, y);
  y += 5;
  y = paragraphs(doc, y, data.latestCommentary.text, logo, data.updateDate);

  // Performance — keep directly after commentary when the final commentary lines
  // have flowed onto a mostly empty page; only add a page if the P table cannot fit.
  y = ensureSpace(doc, y + 5, 62, logo, data.updateDate);
  y = sectionTitle(doc, y, "Portfolio Performance", "CBAM P Range");
  y = drawPerfTable(doc, y, data.performanceP);

  y = ensureSpace(doc, y, 70, logo, data.updateDate);
  y += 4;
  y = sectionTitle(doc, y, "Portfolio Performance", "CBAM R Range");
  y = drawPerfTable(doc, y, data.performanceR);

  // Fund Performance — MTD on its own page; QTD + YTD share a page
  const fundPeriods = data.fundPerformancePeriods?.length
    ? data.fundPerformancePeriods
    : [{ label: data.fundPeriodLabel, bestP: data.bestP, worstP: data.worstP, bestR: data.bestR, worstR: data.worstR }];
  const PERIOD_BLOCK = 120; // approx title + P leagues + R leagues
  fundPeriods.forEach((period, index) => {
    if (index === 0) {
      y = newSection(doc, logo, data.updateDate, y);
    } else if (index === 1) {
      // Start the shared QTD/YTD page
      y = newSection(doc, logo, data.updateDate, y);
    } else {
      // Subsequent periods stay on the QTD/YTD page if there is room
      y = ensureSpace(doc, y + 4, PERIOD_BLOCK, logo, data.updateDate);
    }
    y = sectionTitle(doc, y, "Fund Performance", period.label);
    y = drawFundLeagues(doc, y, "CBAM P", period.bestP, period.worstP);
    y += 3;
    y = ensureSpace(doc, y, 55, logo, data.updateDate);
    y = drawFundLeagues(doc, y, "CBAM R", period.bestR, period.worstR);
  });

  // Positioning
  y = newSection(doc, logo, data.updateDate, y);
  y = sectionTitle(doc, y, "CBAM Positioning vs Benchmark");
  y = drawPositioning(doc, y, data.positioning, logo, data.updateDate);

  // Trade Log — landscape, single line per row
  doc.addPage("a4", "landscape");
  drawHeader(doc, logo, data.updateDate);
  y = 24;
  y = sectionTitle(doc, y, "P Trade Log", data.tradeLogRangeLabel);
  drawTradeLog(doc, y, data.trades);

  // Remove any body pages that ended up with only the branded header.
  try {
    for (let p = doc.getNumberOfPages(); p >= 2; p--) {
      if (!activeBodyPages.has(p)) doc.deletePage(p);
    }
  } catch { /* non-fatal */ }
  activeBodyPages = null;

  // Footer pagination on every page except cover
  const total = doc.getNumberOfPages();
  for (let p = 2; p <= total; p++) {
    doc.setPage(p);
    drawFooter(doc, p - 1, total - 1);
  }

  doc.save(`CBAM_Adviser_Presentation_${format(new Date(), "yyyy-MM-dd")}.pdf`);
}

function drawPerfTable(doc: jsPDF, y: number, rows: PerformanceRow[]): number {
  const startPage = (doc as any).getCurrentPageInfo?.().pageNumber || doc.getNumberOfPages();
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.m, right: PAGE.m, top: 24, bottom: FOOTER_GUARD },
    head: [[
      "Portfolio",
      "MTD","QTD","YTD","1Y",
      "AFI BM","vs MTD","vs QTD","vs YTD","vs 1Y",
      "Int. BM","vs MTD","vs QTD","vs YTD","vs 1Y",
    ]],
    body: rows.map(r => [
      r.portfolio,
      r.mtd, r.qtd, r.ytd, r.oneYear,
      r.afiBenchmark,
      r.vsAfiMtd, r.vsAfiQtd, r.vsAfiYtd, r.vsAfi1Y,
      r.internalBenchmark,
      r.vsIntMtd, r.vsIntQtd, r.vsIntYtd, r.vsInt1Y,
    ]),
    theme: "grid",
    styles: { font: "helvetica", fontSize: 7.5, cellPadding: 1.6, lineColor: RULE, lineWidth: 0.1, textColor: INK, overflow: "visible" },
    headStyles: { fillColor: TEAL, textColor: [255,255,255], fontStyle: "bold", fontSize: 7.5, halign: "center", lineColor: TEAL, overflow: "visible" },
    columnStyles: {
      0: { fontStyle: "bold", halign: "left",   cellWidth: 18, overflow: "visible" },
      1: { halign: "right", cellWidth: 10 }, 2: { halign: "right", cellWidth: 10 }, 3: { halign: "right", cellWidth: 10 }, 4: { halign: "right", cellWidth: 10 },
      5: { halign: "left", textColor: MUTED, fontSize: 6.8, cellWidth: 28, overflow: "ellipsize" },
      6: { halign: "right", cellWidth: 10 }, 7: { halign: "right", cellWidth: 10 }, 8: { halign: "right", cellWidth: 10 }, 9: { halign: "right", cellWidth: 10 },
      10:{ halign: "left", textColor: MUTED, fontSize: 6.8, cellWidth: 16, overflow: "ellipsize" },
      11:{ halign: "right", cellWidth: 10 },12:{ halign: "right", cellWidth: 10 },13:{ halign: "right", cellWidth: 10 },14:{ halign: "right", cellWidth: 10 },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell: (hook) => {
      if (hook.section !== "body") return;
      const idx = hook.column.index;
      const numericCols = new Set([1,2,3,4,6,7,8,9,11,12,13,14]);
      if (numericCols.has(idx)) {
        const c = pctColor(String(hook.cell.raw ?? ""));
        if (c) { hook.cell.styles.textColor = c; hook.cell.styles.fontStyle = "bold"; }
      }
    },
  });
  markBodyPageRange(doc, startPage);
  return (doc as any).lastAutoTable.finalY + 4;
}

function drawFundLeagues(doc: jsPDF, y: number, label: string, best: FundPerformance[], worst: FundPerformance[]): number {
  const startPage = (doc as any).getCurrentPageInfo?.().pageNumber || doc.getNumberOfPages();
  markBodyPage(doc);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...TEAL);
  doc.text(label, PAGE.m + 3, y);
  y += 3;

  const startY = y;
  const colW = (PAGE.w - PAGE.m * 2 - 6) / 2;

  // Best
  autoTable(doc, {
    startY,
    margin: { left: PAGE.m, top: 24, bottom: FOOTER_GUARD },
    tableWidth: colW,
    head: [[{ content: "Best Performing", colSpan: 3 }]],
    body: best.length ? best.map(f => [String(f.rank), f.name, f.return])
                      : [["—","No data",""]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 8, cellPadding: 1.8, lineColor: RULE, lineWidth: 0.1 },
    headStyles: { fillColor: EMERALD, textColor: [255,255,255], halign: "left", fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 8, halign: "center", fontStyle: "bold", textColor: EMERALD }, 2: { halign: "right", fontStyle: "bold", textColor: EMERALD } },
  });
  const endA = (doc as any).lastAutoTable.finalY;

  // Worst
  autoTable(doc, {
    startY,
    margin: { left: PAGE.m + colW + 6, top: 24, bottom: FOOTER_GUARD },
    tableWidth: colW,
    head: [[{ content: "Worst Performing", colSpan: 3 }]],
    body: worst.length ? worst.map(f => [String(f.rank), f.name, f.return])
                       : [["—","No data",""]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 8, cellPadding: 1.8, lineColor: RULE, lineWidth: 0.1 },
    headStyles: { fillColor: RED, textColor: [255,255,255], halign: "left", fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 8, halign: "center", fontStyle: "bold", textColor: RED }, 2: { halign: "right", fontStyle: "bold", textColor: RED } },
  });
  const endB = (doc as any).lastAutoTable.finalY;

  markBodyPageRange(doc, startPage);
  return Math.max(endA, endB) + 4;
}

function drawPositioning(doc: jsPDF, y: number, rows: PositioningRow[], logo: LogoAsset | null, updateDate: string): number {
  const startPage = (doc as any).getCurrentPageInfo?.().pageNumber || doc.getNumberOfPages();
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.m, right: PAGE.m, top: 24, bottom: FOOTER_GUARD },
    pageBreak: "auto",
    rowPageBreak: "avoid",
    head: [["Sector","I","II","III","IV","V"]],
    body: (rows.length ? rows.map(r => [r.sector, r.i, r.ii, r.iii, r.iv, r.v]) : [["No positioning data","","","","",""]]) as any,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 8, cellPadding: 1.5, lineColor: RULE, lineWidth: 0.1, valign: "middle", minCellHeight: 13 },
    headStyles: { fillColor: TEAL, textColor: [255,255,255], halign: "center", fontStyle: "bold", minCellHeight: 7 },
    columnStyles: {
      0: { fontStyle: "bold", halign: "left", cellWidth: 70, valign: "middle" },
      1: { halign: "center" }, 2: { halign: "center" }, 3: { halign: "center" }, 4: { halign: "center" }, 5: { halign: "center" },
    },
    didParseCell: (hook) => {
      if (hook.section !== "body" || hook.column.index === 0) return;
      const cell = hook.cell.raw as { position: string; value: string } | undefined;
      if (cell && typeof cell === "object") hook.cell.text = [""];
    },
    didDrawCell: (hook) => {
      if (hook.section !== "body" || hook.column.index === 0) return;
      const cell = hook.cell.raw as { position: string; value: string } | undefined;
      if (!cell || typeof cell !== "object") return;
      const { x, y: cy, width, height } = hook.cell;
      // Badge — centered horizontally, near the top of the cell
      const badgeW = Math.min(width - 4, 28);
      const badgeH = 5;
      const bx = x + (width - badgeW) / 2;
      const by = cy + 1.5;
      doc.setFillColor(...positionFill(cell.position));
      doc.roundedRect(bx, by, badgeW, badgeH, 1.2, 1.2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.6);
      doc.setTextColor(255, 255, 255);
      doc.text(cell.position, bx + badgeW / 2, by + 3.4, { align: "center" });
      // Percentage — same centered axis as the badge, clearly visible
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      const vc = pctColor(cell.value) || INK;
      doc.setTextColor(...vc);
      doc.text(cell.value, x + width / 2, by + badgeH + 4, { align: "center" });
    },
    didDrawPage: (hook) => {
      if (hook.pageNumber > 1) drawHeader(doc, logo, updateDate);
    },
  });
  markBodyPageRange(doc, startPage);
  return (doc as any).lastAutoTable.finalY + 4;
}

function drawTradeLog(doc: jsPDF, y: number, trades: TradeRow[]) {
  const startPage = (doc as any).getCurrentPageInfo?.().pageNumber || doc.getNumberOfPages();
  // Landscape A4: usable width ≈ 269 mm. Columns sum to ≤ 269 so each row fits on one line.
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.m, right: PAGE.m, top: 24, bottom: FOOTER_GUARD },
    head: [["Date","Manager","Fund","Class","ISIN","P1","P2","P3","P4","P5"]],
    body: trades.length ? trades.map(t => [t.date, t.manager, t.fundName, t.fundClass, t.isin, t.p1, t.p2, t.p3, t.p4, t.p5])
                        : [["—","","No trades in selected range","","","","","","",""]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 7.2, cellPadding: 1.4, lineColor: RULE, lineWidth: 0.1, overflow: "ellipsize", valign: "middle" },
    headStyles: { fillColor: TEAL, textColor: [255,255,255], fontStyle: "bold", fontSize: 7.5, overflow: "visible", halign: "left" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 20, overflow: "visible" },
      1: { cellWidth: 42 },
      2: { cellWidth: 90 },
      3: { cellWidth: 18 },
      4: { cellWidth: 30, font: "courier", fontSize: 6.6 },
      5: { cellWidth: 13, halign: "right", overflow: "visible" },
      6: { cellWidth: 13, halign: "right", overflow: "visible" },
      7: { cellWidth: 13, halign: "right", overflow: "visible" },
      8: { cellWidth: 13, halign: "right", overflow: "visible" },
      9: { cellWidth: 13, halign: "right", overflow: "visible" },
    },
    didParseCell: (hook) => {
      if (hook.section !== "body") return;
      if (hook.column.index >= 5) {
        const c = pctColor(String(hook.cell.raw ?? ""));
        if (c) { hook.cell.styles.textColor = c; hook.cell.styles.fontStyle = "bold"; }
      }
    },
  });
  markBodyPageRange(doc, startPage);
}
